/**
 * Review journey contract (F23): the facts the web app needs to show where a
 * review stands and what to do next. The Worker returns facts only; the web app
 * turns them into stage states, wording and the next step (F23 design.md), so
 * the dashboard, the review overview and every review screen agree.
 * Re-exported from index.ts.
 *
 * Owner: review journey (F23). Implements JRNY-010 and JRNY-011.
 */
import { pagePath } from './paths';
import type { QuestionStatus } from './index';
import type { LivingCadence, LivingPausedReason } from './living';
import type { RunStatus } from './runs';
import type { ApiSearchSource } from './search';

export const JOURNEY_ROUTES = {
  /** GET -> 200 ReviewJourneyResponse; 404 not_found for pages the caller cannot see or pages with no question (JRNY-010). */
  reviewJourney: (slug: string) => `${pagePath(slug)}/journey`,
} as const;

/** One enabled search source and where its strategy stands (JRNY-011). */
export interface JourneySourceFacts {
  source: ApiSearchSource;
  /** Version number of the approved strategy, or null when none is approved. */
  approvedVersion: number | null;
  /** Version number of the newest strategy of any status, or null when none exists. */
  latestVersion: number | null;
  /** True when the newest version is a draft newer than the approved one (or there is no approved one). */
  draftWaiting: boolean;
  /** When the approved version was approved, or null. */
  approvedAt: string | null;
}

export interface JourneyRunFacts {
  id: string;
  status: RunStatus;
  createdAt: string;
  finishedAt: string | null;
}

/** GET JOURNEY_ROUTES.reviewJourney. Counts are whole numbers, never null. */
export interface ReviewJourneyResponse {
  slug: string;
  title: string;
  /** The caller's workspace role, so the web app offers only the actions the server allows. */
  role: 'viewer' | 'editor' | 'owner';
  question: { id: string; status: QuestionStatus; frozenAt: string | null };
  search: { sources: JourneySourceFacts[] };
  runs: {
    /** The queued or running run, if any. */
    active: JourneyRunFacts | null;
    /** The newest run in any final state (completed, failed or cancelled). */
    latest: JourneyRunFacts | null;
    /** The newest completed run. */
    lastCompleted: JourneyRunFacts | null;
  };
  records: {
    /** Records identified across all searches and uploads (PRISMA identification). */
    identified: number;
    /** Records removed as duplicates. */
    duplicatesRemoved: number;
  };
  duplicates: {
    /** Candidate pairs waiting for a person's decision. */
    pendingPairs: number;
  };
  screening: {
    /** Records that need a person: AI disagreement, uncertainty or confirm-mode exclusions. */
    awaitingHuman: number;
    /** Records still waiting for an AI pass or a later stage. */
    pending: number;
    included: number;
    excluded: number;
  };
  /** F16: full texts of the records included at title and abstract (EXTR-016). */
  fullText: {
    /** Records with no open-access full text, or an unreadable one: a person can upload it (EXTR-026). */
    awaitingUpload: number;
    retrieved: number;
  };
  /** F16: studies made from records included at full text, and their extracted values. */
  extraction: {
    studies: number;
    /** Extracted values waiting for a person to confirm, correct or mark not reported (EXTR-075). */
    pendingValues: number;
    /** Studies whose extraction failed (EXTR-059). */
    failed: number;
  };
  /**
   * F17: risk-of-bias assessments, one per study and outcome with a result (RBA-083). The Worker always sends it;
   * optional so facts built before F17 (fixtures, preliminary facts) stay valid.
   */
  rob?: {
    assessments: number;
    /** Assessments with an AI draft or a person's answers, not yet signed off. */
    awaitingSignOff: number;
    signedOff: number;
    /** Assessments whose AI draft failed (RBA-046). */
    failed: number;
  };
  results: {
    /** Studies in the evidence table with at least one usable confirmed result. */
    includedStudies: number;
  };
  /**
   * F06: certainty of evidence per protocol outcome (JRNY-013). Optional so facts built before it
   * existed stay valid; absent means "not reported", and the results stage behaves as before.
   */
  certainty?: {
    /** Protocol outcomes with a confirmed or corrected result in a study that is not retracted. */
    outcomesWithData: number;
    /** Of those, outcomes with a signed-off GRADE assessment. */
    signedOff: number;
    /** Of those without one, outcomes with a draft. */
    drafts: number;
  };
  /**
   * F18: the page's living-update schedule, or null when living updates are off (JRNY-026). Optional so facts
   * built before Phase 3 (fixtures, preliminary facts) stay valid.
   */
  living?: JourneyLivingFacts | null;
}

/** F18 schedule facts for the journey (JRNY-026): when the next scheduled run is, or why there is none. */
export interface JourneyLivingFacts {
  cadence: LivingCadence;
  /** Null while paused. */
  nextRunAt: string | null;
  pausedReason: LivingPausedReason | null;
}
